

















































































































import { Component, Prop, Vue, Watch } from 'vue-property-decorator'
import agora from '@/services/Agora'
import { Room } from '@/services/Rooms'
import Guests, { Guest, GuestState } from '@/services/Guests'
import MessageBox from '@/components/staff/MessageBox.vue'
import marked from 'marked'
import { linkRenderer } from '@/utils/Markdown'
import Timer from '@/components/staff/Timer.vue'
import Loader from '@/components/global/loader.vue'
import PrivateVideo from '@/components/staff/PrivateVideo.vue'

@Component({
  components: { PrivateVideo, Loader, Timer, MessageBox },
})
export default class GuestBox extends Vue {
  @Prop({ required: true }) public userId!: string
  @Prop({ required: true }) public waiterId!: string
  @Prop({ required: true }) public waiterName!: string
  @Prop({ required: true }) public roomsList!: Room[]
  @Prop({ required: true }) public guest!: Guest
  @Prop({ required: true }) public timer!: Timer | null
  @Prop({ required: true }) public activeSeatingId!: string
  @Prop({ required: true }) public isParticipating!: boolean
  @Prop({ required: true }) public numberOfGuestsPerRoom!: { [index: string]: number }

  public restaurantId: string = ''
  public messageToGuest: string = ''
  public interval: number | undefined
  public canSeeItself: boolean = false
  public now: number = new Date().getTime()
  public isInFocus: boolean = !!this.guest.watchingVideoId

  mounted() {
    this.restaurantId = this.$router.currentRoute.params.restaurantId
    this.interval = setInterval(() => {
      this.now = new Date().getTime()
    }, 1000)
  }

  beforeDestroy() {
    agora.stop(this.guest.id)

    if (this.interval) {
      clearInterval(this.interval)
    }
  }

  handleRoomChange(evt: Event) {
    if (!evt.target) {
      return
    }

    const value = (evt.target as HTMLSelectElement).value + ''
    const roomId = value === 'prive' ? null : value
    this.moveRoom(this.userId, roomId)
    if (this.guest.state === GuestState.Service) {
      // If in Service, move with the guest to the new room
      this.$emit('movedGuestToRoom', roomId, this.userId)
    }
  }

  async handleCanSeeItselfChange(evt: Event) {
    if (!evt.target) {
      return
    }
    await Guests.setGuestCanSeeItself(
      this.restaurantId,
      this.activeSeatingId,
      this.userId,
      this.canSeeItself
    )
  }

  public async moveRoom(userId: string, room: string | null) {
    await Guests.moveGuestToRoom(
      this.restaurantId,
      this.activeSeatingId,
      userId,
      room,
      this.waiterId,
      this.waiterName
    )
  }

  convertToReadable(label: string) {
    switch (label) {
      case 'idle':
        return '-'
      case 'welcome':
        return this.$i18n.t('staff.guestBox.stateWelcome')
      case 'service':
        return this.$i18n.t('staff.guestBox.stateService')
      case GuestState.Returning:
        return this.$i18n.t('staff.guestBox.stateReturning')
      case GuestState.WatchingVideo:
        return this.$i18n.t('staff.guestBox.stateWatchingVideo')
    }
    return label
  }

  public async setState(state: string) {
    const waiterId = state === 'service' ? this.waiterId : null
    const waiterName = state === 'service' ? this.waiterName : null
    // Reset every time state is changed, i.e. 1 on 1 mode changes
    this.canSeeItself = false
    this.messageToGuest = ''
    await Guests.setGuestState(
      this.restaurantId,
      this.activeSeatingId,
      this.userId,
      state,
      this.canSeeItself,
      waiterId,
      waiterName
    )

    if (state === 'service') {
      agora.streamAudio(this.userId, true)
      this.$emit('input', this.guest)
    } else if (this.isParticipating) {
      agora.streamAudio(this.userId, true)
      this.$emit('input', null)
    } else {
      agora.streamAudio(this.userId, false)
      this.$emit('input', null)
    }
  }

  @Watch('isParticipating')
  handleParticipationChange() {
    if (this.isParticipating) {
      // enable everybody else's sound
      agora.streamAudio(this.userId, true)
    } else {
      // disable everybody's sound again
      agora.streamAudio(this.userId, false)
    }
  }

  @Watch('messageToGuest')
  handleMessageToGuest() {
    Guests.updateMessageToGuest(
      this.restaurantId,
      this.activeSeatingId,
      this.userId,
      this.messageToGuest
    )
  }

  @Watch('guest.watchingVideoId')
  handleWatchingVideoId(val: string) {
    if (!val) {
      this.stopWatchingVideo()
    } else {
      this.startWatchingVideo(val)
    }
  }

  public get occupied() {
    return this.guest.waiterId && this.guest.waiterId !== this.waiterId
  }

  public get guestTimer() {
    if (!this.guest || !this.guest.sessionStartedOn) {
      return null
    }

    const seconds = (this.now - this.guest.sessionStartedOn) / 1000
    return new Date(seconds * 1000).toISOString().substr(11, 8)
  }

  public endSession() {
    if (!confirm(this.$i18n.t('staff.guestBox.confirmFinish') + '')) {
      return
    }

    Guests.setEndGuestSession(
      this.restaurantId,
      this.activeSeatingId,
      this.userId,
      this.waiterId,
      this.waiterName,
      true
    )
  }

  public refreshGuest() {
    if (!confirm(this.$i18n.t('staff.guestBox.confirmRefresh') + '')) {
      return
    }

    Guests.setGuestRefreshSession(this.restaurantId, this.activeSeatingId, this.userId, true)
  }

  public clearGuestMessage(e: MouseEvent) {
    // Stop when clicked on a link in the message
    if ((e.target as HTMLElement).tagName === 'A') {
      return
    }
    this.messageToGuest = ''
  }

  public markThis(txt: string) {
    marked.setOptions({
      renderer: linkRenderer,
    })
    return marked(txt)
  }

  public startWatchingVideo(videoId: string) {
    this.isInFocus = true
    Guests.setWatchingVideoId(this.restaurantId, this.activeSeatingId, this.userId, videoId)
  }

  public stopWatchingVideo() {
    this.isInFocus = false
    Guests.setWatchingVideoId(this.restaurantId, this.activeSeatingId, this.userId, null)
  }
}
